import {
  BasicButton,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CopiableValue } from "./CopiableValue";
import {
  SUCCESSFACTORS_HELP_URL,
  SUCCESSFACTORS_SAMPLE_REQUEST,
  SUCCESSFACTORS_SAMPLE_RESPONSE,
} from "../webhookSample";

interface CustomerGroup {
  id: string;
  name: string;
}

interface Props {
  showCredentialFields: boolean;
  showGeneratedSecrets: boolean;
  webhookUrl: string;
  webhookUsername: string;
  webhookPassword: string;
  storedWebhookPassword: string;
  customerGroups?: CustomerGroup[];
  isCustomerGroupsLoading: boolean;
  customerGroupsError: unknown;
  onRetryCustomerGroups: () => void;
  isTeamDisabled: boolean;
}

export function SuccessFactorsInboundSection({
  showCredentialFields,
  showGeneratedSecrets,
  webhookUrl,
  webhookUsername,
  webhookPassword,
  storedWebhookPassword,
  customerGroups,
  isCustomerGroupsLoading,
  customerGroupsError,
  onRetryCustomerGroups,
  isTeamDisabled,
}: Props) {
  const { t } = useTranslation();
  const [showSample, setShowSample] = useState(false);

  return (
    <>
      <Text style="global/heading-medium">{t("Inbound webhook")}</Text>
      <Text
        style="form/body-primary"
        testID="successfactors-inbound-description"
      >
        {t(
          "Certn automatically generates the Basic Auth credentials that SuccessFactors needs to send candidate invitations to Certn."
        )}
      </Text>

      {showCredentialFields && (
        <>
          {showGeneratedSecrets && (
            <SectionMessage type="warning" icon="error">
              <Trans>
                Copy the webhook password now. You will not be able to view it
                again after you leave this page unless you regenerate
                credentials.
              </Trans>
            </SectionMessage>
          )}
          <CopiableValue
            testID="successfactors-webhook-url"
            label={t("Webhook URL")}
            value={webhookUrl}
          />
          <CopiableValue
            testID="successfactors-webhook-username"
            label={t("Webhook username")}
            value={webhookUsername}
          />
          {showGeneratedSecrets ? (
            <CopiableValue
              testID="successfactors-webhook-password"
              label={t("Webhook password")}
              value={webhookPassword}
            />
          ) : (
            <>
              <CopiableValue
                testID="successfactors-webhook-password"
                label={t("Webhook password")}
                value={storedWebhookPassword}
                isCopyable={false}
              />
              <Text style="form/body-secondary">
                {t(
                  "The password is hidden. Use Regenerate credentials to copy a new password."
                )}
              </Text>
            </>
          )}
        </>
      )}

      <SelectFormikField
        label={t("Default team")}
        testID="successfactors-default-team"
        name="team_id"
        isRequired
        isLoading={isCustomerGroupsLoading}
        isDisabled={isTeamDisabled || Boolean(customerGroupsError)}
      >
        {customerGroups?.map(({ id, name }) => (
          <SelectItem key={id} id={id} textValue={name}>
            {name}
          </SelectItem>
        ))}
      </SelectFormikField>
      {Boolean(customerGroupsError) && (
        <Stack orientation="horizontal" gap="space-4" align="center">
          <Text
            style="form/body-secondary"
            testID="successfactors-customer-groups-error"
          >
            {t("Unable to load")}
          </Text>
          <BasicButton
            type="button"
            testID="successfactors-retry-customer-groups"
            onPress={onRetryCustomerGroups}
          >
            {t("Retry")}
          </BasicButton>
        </Stack>
      )}
      <Text style="form/body-secondary" testID="successfactors-package-hint">
        <Trans>
          Set up your packages in SuccessFactors.{" "}
          <a
            href={SUCCESSFACTORS_HELP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="successfactors-help-link"
          >
            Learn how to set up this integration.
          </a>
        </Trans>
      </Text>

      <BasicButton
        type="button"
        testID="successfactors-toggle-sample"
        onPress={() => setShowSample((current) => !current)}
      >
        {showSample ? t("Hide sample") : t("Show sample")}
      </BasicButton>
      {showSample && (
        <>
          <pre
            data-testid="successfactors-sample-request"
            className={styles.samplePayload}
          >
            {JSON.stringify(SUCCESSFACTORS_SAMPLE_REQUEST, null, 2)}
          </pre>
          <pre
            data-testid="successfactors-sample-response"
            className={styles.samplePayload}
          >
            {JSON.stringify(SUCCESSFACTORS_SAMPLE_RESPONSE, null, 2)}
          </pre>
        </>
      )}
    </>
  );
}

const styles = {
  samplePayload: css({
    margin: 0,
    padding: "var(--space-3)",
    overflowX: "auto",
    background: "var(--surface-raised)",
    fontSize: "12px",
  }),
};
